import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import UnitIconFrame, { resolveUnitLevelBadge } from './UnitIconFrame'

const PLUGIN_KEY = 'berikingdom'
const SETTINGS_KEY = 'berriWorldSettings'
const PICKER_FAVORITES_KEY = 'berriWorldTroopToolPickerFavorites'

const DEFAULT_TROOP = Object.freeze({
    id: '9',
    label: '9: Veteran demon horror',
    itemType: 'troop',
    statLine: 'Troop',
    level: '',
    iconUrl: ''
})
const DEFAULT_RECRUITMENT_SETTINGS = Object.freeze({
    enabled: false,
    selectionValue: '',
    label: '',
    amount: '0'
})
const DEFAULT_ADVISOR_SETTINGS = Object.freeze({
    enabled: false,
    attackCount: '10'
})
const DEFAULT_CAMP_BUILDING_SETTINGS = Object.freeze({
    buildUnitTent: false,
    upgradeUnitTent: false,
    useSkips: false,
    skipType: '30'
})

const DEFAULT_SETTINGS = Object.freeze({
    attackCountThreshold: '1800',
    sendTroopsAmount: '100',
    maxCampTroopTransfer: false,
    useFeathers: false,
    buyTools: true,
    autoCampExpansion: false,
    timeSkipSendType: '30',
    timeSkipSendAmount: '2',
    playerAttackMode: false,
    playerAttackX: '0',
    playerAttackY: '0',
    advisor: DEFAULT_ADVISOR_SETTINGS,
    troop: DEFAULT_TROOP,
    recruitment: DEFAULT_RECRUITMENT_SETTINGS,
    campBuildings: DEFAULT_CAMP_BUILDING_SETTINGS
})

const SKIP_TIME_OPTIONS = Object.freeze([
    { value: '1', label: '1 Minute' },
    { value: '5', label: '5 Minutes' },
    { value: '10', label: '10 Minutes' },
    { value: '30', label: '30 Minutes' },
    { value: '60', label: '1 Hour' },
    { value: '300', label: '5 Hours' },
    { value: '1440', label: '24 Hours' }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const textValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const searchValue = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const extractLevelValue = (statLine, fallback = '') =>
    String(statLine ?? '').match(/Level\s+([0-9]+)/i)?.[1] || textValue(fallback)

const normalizeTroop = value => ({
    id: textValue(value?.id ?? value?.value, DEFAULT_TROOP.id),
    label: textValue(value?.label, DEFAULT_TROOP.label),
    itemType: value?.itemType === 'tool' ? 'tool' : 'troop',
    statLine: textValue(value?.statLine, value?.itemType === 'tool' ? 'Tool' : 'Troop'),
    level: textValue(value?.level, extractLevelValue(value?.statLine, DEFAULT_TROOP.level)),
    iconUrl: textValue(value?.iconUrl),
    assetKey: textValue(value?.assetKey)
})
const normalizeRecruitmentSlot = value => ({
    selectionValue: textValue(value?.selectionValue ?? value?.troopType ?? value?.id, DEFAULT_RECRUITMENT_SETTINGS.selectionValue),
    label: textValue(value?.label ?? value?.troopLabel, DEFAULT_RECRUITMENT_SETTINGS.label),
    amount: textValue(value?.amount ?? value?.slotSize, DEFAULT_RECRUITMENT_SETTINGS.amount)
})
const pickLegacyRecruitmentSlot = source => {
    const rawSlots = Array.isArray(source?.slots) ? source.slots : []
    return rawSlots.find(slot => Boolean(slot?.selectionValue ?? slot?.troopType ?? slot?.id)) ??
        rawSlots.find(slot => Number.parseInt(String(slot?.amount ?? slot?.slotSize ?? '0'), 10) > 0) ??
        rawSlots[0] ??
        source?.slot1 ??
        source
}
const normalizeRecruitmentSettings = value => {
    const source = value && typeof value === 'object' ? value : {}
    const primarySlot = normalizeRecruitmentSlot(pickLegacyRecruitmentSlot(source))

    return {
        enabled: Boolean(source.enabled),
        selectionValue: primarySlot.selectionValue,
        label: primarySlot.label,
        amount: primarySlot.amount
    }
}
const normalizeCampBuildingSettings = value => {
    const source = value && typeof value === 'object' ? value : {}
    const skipType = textValue(source.skipType, DEFAULT_CAMP_BUILDING_SETTINGS.skipType)

    return {
        buildUnitTent: Boolean(source.buildUnitTent ?? source.enabled ?? DEFAULT_CAMP_BUILDING_SETTINGS.buildUnitTent),
        upgradeUnitTent: Boolean(source.upgradeUnitTent ?? source.upgradeEnabled ?? DEFAULT_CAMP_BUILDING_SETTINGS.upgradeUnitTent),
        useSkips: Boolean(source.useSkips ?? DEFAULT_CAMP_BUILDING_SETTINGS.useSkips),
        skipType: SKIP_TIME_OPTIONS.some(option => option.value === skipType) ?
            skipType :
            DEFAULT_CAMP_BUILDING_SETTINGS.skipType
    }
}

const normalizeSettings = pluginState => {
    const source = pluginState?.[SETTINGS_KEY] && typeof pluginState[SETTINGS_KEY] === 'object' ?
        pluginState[SETTINGS_KEY] :
        {}

    return {
        attackCountThreshold: textValue(source.attackCountThreshold, DEFAULT_SETTINGS.attackCountThreshold),
        sendTroopsAmount: textValue(source.sendTroopsAmount, pluginState?.sendTroopsOver === false ? '0' : DEFAULT_SETTINGS.sendTroopsAmount),
        maxCampTroopTransfer: Boolean(source.maxCampTroopTransfer ?? DEFAULT_SETTINGS.maxCampTroopTransfer),
        useFeathers: Boolean(source.useFeathers ?? pluginState?.useLeather ?? DEFAULT_SETTINGS.useFeathers),
        buyTools: Boolean(source.buyTools ?? pluginState?.buyTools ?? DEFAULT_SETTINGS.buyTools),
        autoCampExpansion: Boolean(source.autoCampExpansion ?? DEFAULT_SETTINGS.autoCampExpansion),
        timeSkipSendType: textValue(source.timeSkipSendType, DEFAULT_SETTINGS.timeSkipSendType),
        timeSkipSendAmount: textValue(source.timeSkipSendAmount, DEFAULT_SETTINGS.timeSkipSendAmount),
        playerAttackMode: Boolean(source.playerAttackMode ?? DEFAULT_SETTINGS.playerAttackMode),
        playerAttackX: textValue(source.playerAttackX, DEFAULT_SETTINGS.playerAttackX),
        playerAttackY: textValue(source.playerAttackY, DEFAULT_SETTINGS.playerAttackY),
        advisor: {
            enabled: Boolean(source.advisor?.enabled ?? DEFAULT_ADVISOR_SETTINGS.enabled),
            attackCount: textValue(source.advisor?.attackCount, DEFAULT_ADVISOR_SETTINGS.attackCount)
        },
        troop: normalizeTroop(source.sendTroop ?? source.troop ?? DEFAULT_SETTINGS.troop),
        recruitment: normalizeRecruitmentSettings(source.recruitment),
        campBuildings: normalizeCampBuildingSettings(source.campBuildings)
    }
}

const buildPickerEntries = catalog => {
    const nextEntries = []
    const seenKeys = new Set()

    const appendEntries = (entries, itemType) => {
        for (const item of Array.isArray(entries) ? entries : []) {
            const id = textValue(item?.value ?? item?.id)
            const key = `${itemType}:${id}`
            if (!id || seenKeys.has(key))
                continue

            const rawLabel = textValue(item?.label, id)
            const label = rawLabel.startsWith(`${id}:`) ? rawLabel : `${id}: ${rawLabel}`

            seenKeys.add(key)
            nextEntries.push({
                key,
                id,
                label,
                itemType,
                level: extractLevelValue(item?.statLine, item?.level),
                statLine: textValue(item?.statLine, itemType === 'tool' ? 'Tool' : 'Troop'),
                iconUrl: textValue(item?.iconUrl),
                assetKey: textValue(item?.assetKey),
                searchText: textValue(item?.searchText, `${label} ${item?.statLine ?? ''}`)
            })
        }
    }

    appendEntries(catalog?.attackTroops, 'troop')
    appendEntries(catalog?.defenseTroops, 'troop')
    appendEntries(catalog?.attackTools, 'tool')
    appendEntries(catalog?.defenseTools, 'tool')
    return nextEntries
}

const loadPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsed = JSON.parse(window.localStorage.getItem(PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const savePickerFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

const cardSx = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{children}</Typography>
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>{label}</Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function PickerFilterButton({ icon, label, count, active, onClick }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            startIcon={icon}
            sx={{
                minHeight: 36,
                px: 1.35,
                borderRadius: '10px',
                borderColor: active ? 'rgba(112, 136, 174, 0.56)' : 'rgba(112, 136, 174, 0.34)',
                backgroundColor: active ? 'rgba(74, 91, 120, 0.72)' : 'rgba(42, 54, 78, 0.56)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.96rem',
                gap: 0.8,
                '&:hover': {
                    borderColor: 'rgba(112, 136, 174, 0.58)',
                    backgroundColor: active ? 'rgba(82, 98, 127, 0.78)' : 'rgba(50, 63, 88, 0.68)'
                }
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    px: 0.75,
                    minWidth: 34,
                    minHeight: 24,
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#aebad3',
                    fontWeight: 800,
                    fontSize: '0.95rem'
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function TroopToolPickerDialog({ open, items, onClose, onSelect }) {
    const [query, setQuery] = React.useState('')
    const [filterValue, setFilterValue] = React.useState('all')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadPickerFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        savePickerFavorites(favoriteKeys)
    }, [favoriteKeys])

    const counts = React.useMemo(() => ({
        all: items.length,
        troops: items.filter(entry => entry.itemType === 'troop').length,
        tools: items.filter(entry => entry.itemType === 'tool').length
    }), [items])

    const filteredItems = React.useMemo(() => {
        const normalizedQuery = searchValue(query)

        return items
            .filter(entry => {
                if (filterValue === 'troops' && entry.itemType !== 'troop')
                    return false
                if (filterValue === 'tools' && entry.itemType !== 'tool')
                    return false
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return searchValue(`${entry.label} ${entry.id} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [favoriteKeySet, favoritesOnly, filterValue, items, query])

    const toggleFavorite = itemKey => {
        setFavoriteKeys(currentKeys =>
            currentKeys.includes(itemKey) ?
                currentKeys.filter(currentKey => currentKey !== itemKey) :
                [...currentKeys, itemKey]
        )
    }

    const handleClearFilters = () => {
        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            Troop & Tool Picker
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#9aa7c2', width: 30, height: 30 }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or ID..."
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#edf3ff',
                                fontSize: '0.98rem',
                                fontWeight: 600
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeys.length > 0 || favoritesOnly ? <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> : <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 122,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            '&:hover': {
                                borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.82)' : 'rgba(112, 136, 174, 0.5)',
                                backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.2)' : 'rgba(50, 63, 88, 0.7)'
                            }
                        }}
                    >
                        Favorites
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleClearFilters}
                        sx={{
                            minWidth: 108,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#ff4858',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Clear
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Stack direction="row" spacing={0.85}>
                        <PickerFilterButton icon={<FilterAltRoundedIcon sx={{ fontSize: 19 }} />} label="All" count={counts.all} active={filterValue === 'all'} onClick={() => setFilterValue('all')} />
                        <PickerFilterButton icon={<GroupsRoundedIcon sx={{ fontSize: 19 }} />} label="Troops" count={counts.troops} active={filterValue === 'troops'} onClick={() => setFilterValue('troops')} />
                        <PickerFilterButton icon={<HandymanRoundedIcon sx={{ fontSize: 19 }} />} label="Tools" count={counts.tools} active={filterValue === 'tools'} onClick={() => setFilterValue('tools')} />
                    </Stack>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem' }}>
                        {`${filteredItems.length} results`}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(123, 147, 186, 0.28)',
                        backgroundColor: 'rgba(31, 43, 67, 0.92)',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {filteredItems.map((item, index) => {
                        const isFavorite = favoriteKeySet.has(item.key)
                        return (
                            <Box
                                key={item.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect(item)
                                    onClose()
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        onSelect(item)
                                        onClose()
                                    }
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredItems.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <UnitIconFrame
                                        iconUrl={item.iconUrl}
                                        itemType={item.itemType}
                                        level={item.itemType === 'troop' ? resolveUnitLevelBadge(item) : ''}
                                        size={52}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 0.6 }}>
                                            <Typography sx={{ color: '#5ea3ff', fontWeight: 800, fontSize: '1.01rem' }}>
                                                {`#${index + 1}`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 0.95,
                                                    minHeight: 28,
                                                    borderRadius: '10px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid rgba(130, 147, 177, 0.45)',
                                                    color: '#d6deec',
                                                    fontWeight: 700,
                                                    fontSize: '0.94rem'
                                                }}
                                            >
                                                {item.itemType === 'tool' ? 'Tool' : 'Troop'}
                                            </Box>
                                        </Stack>

                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        toggleFavorite(item.key)
                                    }}
                                    sx={{ color: isFavorite ? '#ff6e82' : '#9aa6bf', width: 34, height: 34 }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    })}
                </Box>

                <Box
                    sx={{
                        mt: 1.45,
                        px: 2.2,
                        py: 1.45,
                        mx: -2.2,
                        borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        Click to select - Favorite to bookmark - Type an ID manually in the input field
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function SearchField({ label, helper, value, placeholder, onOpen, afterInput }) {
    return (
        <Box>
            <FieldTitle>{label}</FieldTitle>
            <TextField
                fullWidth
                value={value}
                placeholder={placeholder}
                onClick={onOpen}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={onOpen} edge="end" sx={{ color: '#d9e4fb' }}>
                                <SearchRoundedIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={inputSx}
            />
            {afterInput}
            <Helper>{helper}</Helper>
        </Box>
    )
}

export default function BerriWorldSettingsPanel({ userPlugins, onChange, appLanguage = 'en' }) {
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const [catalog, setCatalog] = React.useState({ attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
    const [recruitmentCatalog, setRecruitmentCatalog] = React.useState([])

    const pluginState = userPlugins?.[PLUGIN_KEY]
    const config = React.useMemo(() => normalizeSettings(pluginState), [pluginState])
    const troopAndToolEntries = React.useMemo(() => buildPickerEntries(catalog), [catalog])
    const troopAndToolEntriesByKey = React.useMemo(
        () => new Map(troopAndToolEntries.map(entry => [`${entry.itemType}:${entry.id}`, entry])),
        [troopAndToolEntries]
    )
    const recruitmentOptions = React.useMemo(() =>
        Array.isArray(recruitmentCatalog) ? recruitmentCatalog : [],
    [recruitmentCatalog])
    const recruitmentOptionsByValue = React.useMemo(() =>
        new Map(recruitmentOptions.map(option => [String(option?.value ?? ''), option])),
    [recruitmentOptions])
    const displayedTroop = React.useMemo(
        () => troopAndToolEntriesByKey.get(`${config.troop.itemType}:${config.troop.id}`) ?? config.troop,
        [config.troop, troopAndToolEntriesByKey]
    )

    const update = React.useCallback(producer => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}

        const nextConfig = normalizeSettings(nextPlugins[PLUGIN_KEY])
        producer(nextConfig)

        nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = nextConfig
        nextPlugins[PLUGIN_KEY].useLeather = Boolean(nextConfig.useFeathers)
        nextPlugins[PLUGIN_KEY].buyTools = Boolean(nextConfig.buyTools)
        nextPlugins[PLUGIN_KEY].sendTroopsOver = Boolean(nextConfig.maxCampTroopTransfer) || Number.parseInt(nextConfig.sendTroopsAmount, 10) > 0

        onChange(nextPlugins)
    }, [onChange, userPlugins])
    React.useEffect(() => {
        let cancelled = false

        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => response.ok ? response.json() : { attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
            .then(data => {
                if (!cancelled)
                    setCatalog(data && typeof data === 'object' ? data : { attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
            })
            .catch(() => {
                if (!cancelled)
                    setCatalog({ attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
            })

        fetch(`/berimond-recruitment-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => response.ok ? response.json() : [])
            .then(data => {
                if (!cancelled)
                    setRecruitmentCatalog(Array.isArray(data) ? data : [])
            })
            .catch(() => {
                if (!cancelled)
                    setRecruitmentCatalog([])
            })

        return () => {
            cancelled = true
        }
    }, [appLanguage])

    const troopChips = (
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.05 }}>
            <UnitIconFrame
                iconUrl={displayedTroop.iconUrl}
                itemType={displayedTroop.itemType}
                level={displayedTroop.itemType === 'troop' ? resolveUnitLevelBadge(displayedTroop) : ''}
                size={36}
            />
            <Chip
                size="small"
                label={displayedTroop.itemType === 'tool' ? 'Tool' : 'Troop'}
                sx={{
                    height: 28,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(66, 84, 116, 0.78)',
                    color: '#e6eefb',
                    fontWeight: 700
                }}
            />
            {displayedTroop.level ? (
                <Chip
                    size="small"
                    label={`Level ${displayedTroop.level}`}
                    sx={{
                        height: 28,
                        borderRadius: '10px',
                        backgroundColor: 'rgba(66, 84, 116, 0.52)',
                        color: '#aebad3',
                        fontWeight: 700
                    }}
                />
            ) : null}
        </Stack>
    )
    return (
        <>
            <Box sx={cardSx}>
                <Stack spacing={4.1} sx={{ p: { xs: 2.3, md: 3.3 }, position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" spacing={1.15} alignItems="flex-start">
                        <StarBorderRoundedIcon sx={{ color: '#f5f7ff', mt: 0.22 }} />
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1.08rem', fontWeight: 800 }}>
                                Berri World Settings
                            </Typography>
                            <Typography sx={{ color: '#9fb0cf', fontSize: '0.98rem', mt: 0.4 }}>
                                Configure Berimond attacks, troop management, and player attack options
                            </Typography>
                        </Box>
                    </Stack>

                    <Box>
                        <SectionHeading>Attack Settings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Attack Count Threshold</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.attackCountThreshold}
                                    onChange={event => update(nextConfig => { nextConfig.attackCountThreshold = event.target.value })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                                <Helper>Number of attacks before stopping.</Helper>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ToggleField
                                    checked={config.advisor.enabled}
                                    onChange={value => update(nextConfig => {
                                        nextConfig.advisor = {
                                            ...DEFAULT_ADVISOR_SETTINGS,
                                            ...(nextConfig.advisor ?? {}),
                                            enabled: value
                                        }
                                    })}
                                    label="Advisor-style Attack Train"
                                    helper="Runs consecutive Beri World attacks without waiting for a commander to return. Berimond has no native advisor token, so this mode is emulated by the bot."
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Advisor Train Attacks</FieldTitle>
                                <TextField
                                    fullWidth
                                    disabled={!config.advisor.enabled}
                                    value={config.advisor.attackCount}
                                    onChange={event => update(nextConfig => {
                                        nextConfig.advisor = {
                                            ...DEFAULT_ADVISOR_SETTINGS,
                                            ...(nextConfig.advisor ?? {}),
                                            attackCount: event.target.value
                                        }
                                    })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric', min: 2, max: 9999 }}
                                />
                                <Helper>Number of attacks grouped and reported as one advisor-style train.</Helper>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Troop Settings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={7}>
                                <SearchField
                                    label="Send Troop ID"
                                    helper="Troop ID to send for attacks"
                                    value={displayedTroop.label}
                                    placeholder="Select troop ID"
                                    onOpen={() => setPickerOpen(true)}
                                    afterInput={troopChips}
                                />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ToggleField
                                    checked={config.useFeathers}
                                    onChange={value => update(nextConfig => { nextConfig.useFeathers = value })}
                                    label="Use Feathers"
                                    helper="Use feathers for attacks"
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Send Troops Amount</FieldTitle>
                                <TextField
                                    fullWidth
                                    disabled={config.maxCampTroopTransfer}
                                    value={config.sendTroopsAmount}
                                    onChange={event => update(nextConfig => { nextConfig.sendTroopsAmount = event.target.value })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                                <Helper>Normal transfer size. When max camp transfer is enabled, the bot ignores this value and sends up to the current free camp capacity.</Helper>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ToggleField
                                    checked={config.maxCampTroopTransfer}
                                    onChange={value => update(nextConfig => { nextConfig.maxCampTroopTransfer = value })}
                                    label="Max Camp Troop Transfer"
                                    helper="Sends as many attack troops as Berimond camp capacity allows, while prioritizing the saved attack template soldiers."
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Recruitment</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={6}>
                                <ToggleField
                                    checked={config.recruitment.enabled}
                                    onChange={value => update(nextConfig => { nextConfig.recruitment.enabled = value })}
                                    label="Auto Recruit In Berimond"
                                    helper="Checks every 5 minutes and queues troops when a free Berimond compartment is found."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: 2.1,
                                        borderRadius: '14px',
                                        border: '1px solid rgba(129, 146, 178, 0.18)',
                                        background: 'rgba(22, 33, 53, 0.42)'
                                    }}
                                >
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 1.7 }}>
                                        All Free Compartments
                                    </Typography>
                                    <Grid container spacing={2.2}>
                                        <Grid item xs={12} md={8}>
                                            <FieldTitle>Soldier</FieldTitle>
                                            <TextField
                                                select
                                                fullWidth
                                                disabled={!config.recruitment.enabled}
                                                value={config.recruitment.selectionValue}
                                                onChange={event => {
                                                    const nextValue = event.target.value
                                                    const matchedOption = recruitmentOptionsByValue.get(String(nextValue))
                                                    update(nextConfig => {
                                                        nextConfig.recruitment = normalizeRecruitmentSettings(nextConfig.recruitment)
                                                        nextConfig.recruitment.selectionValue = nextValue
                                                        nextConfig.recruitment.label = matchedOption?.label ?? ''
                                                    })
                                                }}
                                                sx={inputSx}
                                            >
                                                <MenuItem value="">Disabled</MenuItem>
                                                {config.recruitment.selectionValue && !recruitmentOptionsByValue.get(String(config.recruitment.selectionValue)) ? (
                                                    <MenuItem value={config.recruitment.selectionValue}>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <UnitIconFrame itemType="troop" size={34} />
                                                            <Typography>{textValue(config.recruitment.label, config.recruitment.selectionValue)}</Typography>
                                                        </Stack>
                                                    </MenuItem>
                                                ) : null}
                                                {recruitmentOptions.map(option => (
                                                    <MenuItem key={String(option?.value ?? '')} value={String(option?.value ?? '')}>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <UnitIconFrame
                                                                iconUrl={option.iconUrl}
                                                                itemType="troop"
                                                                level={resolveUnitLevelBadge(option)}
                                                                size={34}
                                                            />
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                    {option.label}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                    {`ID ${option.value}`}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <Helper>Only soldiers that can be recruited in Berimond are shown here.</Helper>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FieldTitle>Amount</FieldTitle>
                                            <TextField
                                                fullWidth
                                                disabled={!config.recruitment.enabled}
                                                value={config.recruitment.amount}
                                                onChange={event => update(nextConfig => {
                                                    nextConfig.recruitment = normalizeRecruitmentSettings(nextConfig.recruitment)
                                                    nextConfig.recruitment.amount = event.target.value
                                                })}
                                                sx={inputSx}
                                                inputProps={{ inputMode: 'numeric' }}
                                            />
                                            <Helper>Requested troop count for every free Berimond compartment.</Helper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Purchase Settings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={5}>
                                <ToggleField
                                    checked={config.buyTools}
                                    onChange={value => update(nextConfig => { nextConfig.buyTools = value })}
                                    label="Buy Tools"
                                    helper="Automatically buy tools"
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <ToggleField
                                    checked={config.autoCampExpansion}
                                    onChange={value => update(nextConfig => { nextConfig.autoCampExpansion = value })}
                                    label="Auto Camp Expansion"
                                    helper="Expands the Berimond camp when wood and stone are available. Uses resource expansion only; never ruby expansion."
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Camp Buildings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={4}>
                                <ToggleField
                                    checked={config.campBuildings.buildUnitTent}
                                    onChange={value => update(nextConfig => {
                                        nextConfig.campBuildings = normalizeCampBuildingSettings(nextConfig.campBuildings)
                                        nextConfig.campBuildings.buildUnitTent = value
                                    })}
                                    label="Build Unit Tent"
                                    helper="Builds the Berimond Unit Tent when a free camp tile and wood/stone are available."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ToggleField
                                    checked={config.campBuildings.upgradeUnitTent}
                                    onChange={value => update(nextConfig => {
                                        nextConfig.campBuildings = normalizeCampBuildingSettings(nextConfig.campBuildings)
                                        nextConfig.campBuildings.upgradeUnitTent = value
                                    })}
                                    label="Upgrade Unit Tents"
                                    helper="Upgrades existing Berimond Unit Tents with wood/stone only."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ToggleField
                                    checked={config.campBuildings.useSkips}
                                    onChange={value => update(nextConfig => {
                                        nextConfig.campBuildings = normalizeCampBuildingSettings(nextConfig.campBuildings)
                                        nextConfig.campBuildings.useSkips = value
                                    })}
                                    label="Use Build Time Skips"
                                    helper="Applies the selected minute skip to Unit Tent build or upgrade timers."
                                />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <FieldTitle>Unit Tent Skip Type</FieldTitle>
                                <TextField
                                    select
                                    fullWidth
                                    disabled={!config.campBuildings.useSkips}
                                    value={config.campBuildings.skipType}
                                    onChange={event => update(nextConfig => {
                                        nextConfig.campBuildings = normalizeCampBuildingSettings(nextConfig.campBuildings)
                                        nextConfig.campBuildings.skipType = event.target.value
                                    })}
                                    sx={inputSx}
                                >
                                    {SKIP_TIME_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Helper>Only this skip type is used for building and upgrading Unit Tents.</Helper>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Time Skip Settings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={5}>
                                <FieldTitle>Time Skip Send Type</FieldTitle>
                                <TextField
                                    select
                                    fullWidth
                                    value={config.timeSkipSendType}
                                    onChange={event => update(nextConfig => { nextConfig.timeSkipSendType = event.target.value })}
                                    sx={inputSx}
                                >
                                    {SKIP_TIME_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Helper>Type of time skip to use when sending troops</Helper>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Time Skip Send Amount</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.timeSkipSendAmount}
                                    onChange={event => update(nextConfig => { nextConfig.timeSkipSendAmount = event.target.value })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                                <Helper>Amount of time skips to use when sending troops</Helper>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <SectionHeading>Player Attack Settings</SectionHeading>
                        <Grid container spacing={2.8}>
                            <Grid item xs={12} md={5}>
                                <ToggleField
                                    checked={config.playerAttackMode}
                                    onChange={value => update(nextConfig => { nextConfig.playerAttackMode = value })}
                                    label="Player Attack Mode"
                                    helper="Enable player attack mode"
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Player Attack X</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.playerAttackX}
                                    onChange={event => update(nextConfig => { nextConfig.playerAttackX = event.target.value })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                                <Helper>X coordinate for player attacks</Helper>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <FieldTitle>Player Attack Y</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.playerAttackY}
                                    onChange={event => update(nextConfig => { nextConfig.playerAttackY = event.target.value })}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                                <Helper>Y coordinate for player attacks</Helper>
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </Box>

            <TroopToolPickerDialog
                open={pickerOpen}
                items={troopAndToolEntries}
                onClose={() => setPickerOpen(false)}
                onSelect={item => update(nextConfig => { nextConfig.troop = normalizeTroop(item) })}
            />
        </>
    )
}
